import { Button, Prompt } from '@root/components'
import { SettingsSubPageLayout } from '@pages/SettingsPage/components/SettingsSubPageLayout'
import { useBusinessContext, useFeature } from '@root/context'
import { useResetCacheFormValues } from '@root/hooks'
import {
  DEBT_COLLECTION_DELAY_MIN_COMPANY_DAYS,
  DEBT_COLLECTION_DELAY_MIN_PERSON_DAYS,
  SOFT_REMINDER_MAX_DAYS,
  SOFT_REMINDER_MAX_STEPS
} from '@constants'
import { IBusiness, updateBusiness } from '@root/query'
import { businessDefaultInvoicingLanguage, invoiceFormCacheKey } from '@root/utils'
import React, { useState } from 'react'
import { FormProvider, useFieldArray, useForm } from 'react-hook-form'
import { toast } from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { FaCheck, FaFlag, FaGavel, FaMinusCircle, FaPlus } from 'react-icons/fa'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Timeline,
  TimelineBody,
  TimelineNode,
  TimelineRail,
  TimelineRow,
  TimelineStem
} from '@root/components/TimelineRail/TimelineRail.styled'
import InvoiceSettingDebtCollectionOnBoardKravia from './InvoiceSettingDebtCollectionOnBoardKravia'
import { CollectionHandoverCard } from './CollectionHandoverCard'
import { ReminderStepCard } from './ReminderStepCard'
import {
  lastReminderOverlapsCollection,
  PaymentRemindersFormValues
} from './paymentRemindersFormValues'
import {
  AddStepRow,
  AnchorHint,
  AnchorTitle,
  EmptyReminders,
  Eyebrow,
  PageBody,
  PageSubtitle,
  SavePrompt,
  SavePromptGroup,
  SavePromptItem,
  SettingsForm
} from './PaymentRemindersAndCollectionForm.styled'

const FORM_ID = 'payment-reminders-settings-form'

interface Props {
  isKraviaVerified: boolean
  children?: React.ReactNode
}

export const PaymentRemindersAndCollectionForm: React.FC<Props> = ({
  isKraviaVerified,
  children
}) => {
  const [t] = useTranslation()
  const { enabled: kraviaEnabled } = useFeature('kravia')
  const { slug, data: business } = useBusinessContext()
  const defaultLanguage = businessDefaultInvoicingLanguage(business)
  const queryClient = useQueryClient()
  const { resetCache: resetInvoiceFormCache } = useResetCacheFormValues(invoiceFormCacheKey(slug))
  const [showKraviaOnboard, setShowKraviaOnboard] = useState(false)
  const [pendingSave, setPendingSave] = useState<PaymentRemindersFormValues | null>(null)

  const formDefaultValues = (data: Partial<IBusiness>): PaymentRemindersFormValues => ({
    is_automatic_debt_collection_enabled: data?.is_automatic_debt_collection_enabled || false,
    soft_reminder_steps: data?.soft_reminder_steps?.length
      ? data.soft_reminder_steps.map(step => ({
          days_after_due: step.days_after_due,
          message: step.message?.length ? step.message : [{ key: defaultLanguage, value: '' }]
        }))
      : [],
    debt_collection_delay_days_company:
      data?.debt_collection_delay_days_company ?? DEBT_COLLECTION_DELAY_MIN_COMPANY_DAYS,
    debt_collection_delay_days_person:
      data?.debt_collection_delay_days_person ?? DEBT_COLLECTION_DELAY_MIN_PERSON_DAYS
  })

  const form = useForm<PaymentRemindersFormValues>({
    defaultValues: formDefaultValues(business)
  })

  const {
    handleSubmit,
    control,
    reset,
    getValues,
    setError,
    formState: { isDirty, errors }
  } = form

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'soft_reminder_steps'
  })

  const updateDataMutation = useMutation<Partial<IBusiness>, Error, Partial<IBusiness>>({
    mutationFn: data => updateBusiness({ slug }, data),
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey: [slug] })
      resetInvoiceFormCache()
      reset(formDefaultValues(data))
      setPendingSave(null)
    }
  })

  const persistSettings = (data: PaymentRemindersFormValues) => {
    const payload: Partial<IBusiness> = {
      soft_reminder_steps: data.soft_reminder_steps
    }
    if (kraviaEnabled) {
      payload.debt_collection_delay_days_company = data.debt_collection_delay_days_company
      payload.debt_collection_delay_days_person = data.debt_collection_delay_days_person
    }
    if (kraviaEnabled && isKraviaVerified) {
      payload.is_automatic_debt_collection_enabled = data.is_automatic_debt_collection_enabled
    }
    const promise = updateDataMutation.mutateAsync(payload)
    toast.promise(promise, {
      loading: t('general.loading'),
      success: t('general.changesSaved'),
      error: t('general.error')
    })
  }

  const requestSave = (data: PaymentRemindersFormValues) => {
    if (
      kraviaEnabled &&
      isKraviaVerified &&
      lastReminderOverlapsCollection({
        is_automatic_debt_collection_enabled: Boolean(data.is_automatic_debt_collection_enabled),
        soft_reminder_steps: data.soft_reminder_steps,
        debt_collection_delay_days_company: Number(data.debt_collection_delay_days_company) || 0,
        debt_collection_delay_days_person: Number(data.debt_collection_delay_days_person) || 0
      })
    ) {
      const lastIndex = (data.soft_reminder_steps || []).length - 1
      if (lastIndex >= 0) {
        setError(`soft_reminder_steps.${lastIndex}.days_after_due`, {
          type: 'validate',
          message: t('settings.invoicing.collectionHandoverTooSoon')
        })
      }
      return
    }
    setPendingSave(data)
  }

  const addStep = () => {
    const steps = getValues('soft_reminder_steps') || []
    const lastDays = Number(steps[steps.length - 1]?.days_after_due)
    const previous = Number.isFinite(lastDays) && lastDays >= 1 ? lastDays : 3
    append({
      days_after_due: steps.length ? Math.min(previous + 1, SOFT_REMINDER_MAX_DAYS) : 3,
      message: [{ key: defaultLanguage, value: '' }]
    })
  }

  const layout = {
    title: t('settings.sections.paymentRemindersAndCollection'),
    subTitle: <PageSubtitle>{t('settings.invoicing.paymentRemindersSubtitle')}</PageSubtitle>
  }

  if (kraviaEnabled && showKraviaOnboard) {
    return (
      <SettingsSubPageLayout
        {...layout}
        contentPadding="none"
        footer={
          <Button type="button" isSecondary onClick={() => setShowKraviaOnboard(false)}>
            {t('general.cancel')}
          </Button>
        }
      >
        <PageBody>
          <InvoiceSettingDebtCollectionOnBoardKravia />
        </PageBody>
      </SettingsSubPageLayout>
    )
  }

  return (
    <FormProvider {...form}>
      <SettingsSubPageLayout
        {...layout}
        contentPadding="none"
        footer={
          <Button
            type="button"
            intent="success"
            icon={<FaCheck />}
            disabled={!isDirty || updateDataMutation.isPending}
            showSpinner={updateDataMutation.isPending}
            onClick={handleSubmit(requestSave)}
          >
            {t('settings.invoicing.saveChanges')}
          </Button>
        }
      >
        {children}
        <PageBody>
          <SettingsForm id={FORM_ID} method="post" onSubmit={handleSubmit(requestSave)}>
            <Timeline>
              <TimelineRow>
                <TimelineRail>
                  <TimelineNode $tone="neutral">
                    <FaFlag />
                  </TimelineNode>
                  <TimelineStem />
                </TimelineRail>
                <TimelineBody $pad="sm">
                  <AnchorTitle>{t('settings.invoicing.dueDate')}</AnchorTitle>
                  <AnchorHint>{t('settings.invoicing.dueDateHint')}</AnchorHint>
                </TimelineBody>
              </TimelineRow>

              {fields.map((field, index) => {
                const addStepVisible = fields.length > 0 && fields.length < SOFT_REMINDER_MAX_STEPS
                const isLastReminder = index === fields.length - 1
                return (
                  <TimelineRow key={field.id}>
                    <TimelineRail>
                      <TimelineNode $tone="info">{index + 1}</TimelineNode>
                      {(!isLastReminder || addStepVisible || kraviaEnabled) && <TimelineStem />}
                    </TimelineRail>
                    <TimelineBody>
                      <ReminderStepCard
                        index={index}
                        onRemove={() => remove(index)}
                        daysError={errors.soft_reminder_steps?.[index]?.days_after_due?.message}
                      />
                    </TimelineBody>
                  </TimelineRow>
                )
              })}

              {fields.length === 0 && (
                <TimelineRow>
                  <TimelineRail>
                    <TimelineNode $tone="dashed">
                      <FaPlus />
                    </TimelineNode>
                    {kraviaEnabled && <TimelineStem />}
                  </TimelineRail>
                  <TimelineBody>
                    <EmptyReminders>
                      <AnchorTitle>{t('settings.invoicing.noRemindersYet')}</AnchorTitle>
                      <AnchorHint>{t('settings.invoicing.noRemindersDescription')}</AnchorHint>
                      <Button
                        type="button"
                        intent="primary"
                        size="sm"
                        icon={<FaPlus />}
                        onClick={addStep}
                      >
                        {t('settings.invoicing.addFirstReminder')}
                      </Button>
                    </EmptyReminders>
                  </TimelineBody>
                </TimelineRow>
              )}

              {fields.length > 0 && fields.length < SOFT_REMINDER_MAX_STEPS && (
                <TimelineRow>
                  <TimelineRail>
                    <TimelineNode $tone="dashed">
                      <FaPlus />
                    </TimelineNode>
                    {kraviaEnabled && <TimelineStem />}
                  </TimelineRail>
                  <AddStepRow>
                    <Button type="button" isSecondary size="sm" onClick={addStep}>
                      {t('settings.invoicing.addAnotherReminder')}
                    </Button>
                  </AddStepRow>
                </TimelineRow>
              )}

              {kraviaEnabled && (
                <TimelineRow>
                  <TimelineRail>
                    <TimelineNode $tone="collection">
                      <FaGavel />
                    </TimelineNode>
                  </TimelineRail>
                  <TimelineBody $pad="lg">
                    <CollectionHandoverCard
                      kraviaVerified={isKraviaVerified}
                      onActivate={() => setShowKraviaOnboard(true)}
                    />
                  </TimelineBody>
                </TimelineRow>
              )}
            </Timeline>
          </SettingsForm>
        </PageBody>
      </SettingsSubPageLayout>
      <Prompt
        isVisible={Boolean(pendingSave)}
        onClose={() => {
          if (!updateDataMutation.isPending) setPendingSave(null)
        }}
        title={t('settings.invoicing.remindersOnlyNewInvoicesTitle')}
        description={
          <SavePrompt>
            <SavePromptGroup>
              <Eyebrow>{t('settings.invoicing.remindersSaveUpdatesEyebrow')}</Eyebrow>
              <SavePromptItem $tone="apply">
                <FaCheck />
                <span>{t('settings.invoicing.remindersSaveExistingSchedule')}</span>
              </SavePromptItem>
              <SavePromptItem $tone="apply">
                <FaCheck />
                <span>{t('settings.invoicing.remindersSaveNewInvoices')}</span>
              </SavePromptItem>
            </SavePromptGroup>
            <SavePromptGroup>
              <Eyebrow $tone="muted">{t('settings.invoicing.remindersSaveDoesNotEyebrow')}</Eyebrow>
              <SavePromptItem $tone="skip">
                <FaMinusCircle />
                <span>{t('settings.invoicing.remindersSaveOffInvoices')}</span>
              </SavePromptItem>
            </SavePromptGroup>
          </SavePrompt>
        }
        buttons={[
          {
            text: t('general.cancel'),
            action: () => setPendingSave(null),
            disabled: updateDataMutation.isPending
          },
          {
            text: t('settings.invoicing.saveChanges'),
            intent: 'success',
            icon: <FaCheck />,
            action: () => pendingSave && persistSettings(pendingSave),
            disabled: updateDataMutation.isPending,
            showSpinner: updateDataMutation.isPending
          }
        ]}
      />
    </FormProvider>
  )
}
